import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Calendar,
  Clock,
  MapPin,
  Ticket,
  Accessibility,
  ArrowLeft,
  ArrowUpRight,
  Loader2,
  ExternalLink,
  Plus,
  Minus,
  Sparkles,
} from "lucide-react";
import { Container, FadeIn, SectionLabel } from "@/components/Section";
import SEOHead from "@/components/SEOHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_QTY = 8;

function formatDate(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso;
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export default function EventDetail() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [searchParams] = useSearchParams();

  // Booking state
  const [quantity, setQuantity] = useState(1);
  const [email, setEmail] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/events/${eventId}`)
      .then((r) => {
        if (cancelled) return;
        setEvent(r.data);
        // fetch related (same category, exclude self)
        return axios
          .get(`${API}/events`, { params: { category: r.data.category } })
          .then((rr) => {
            if (cancelled) return;
            setRelated(
              (rr.data || []).filter((x) => x.id !== r.data.id).slice(0, 3),
            );
          })
          .catch(() => {});
      })
      .catch((e) => {
        if (!cancelled)
          setError(e?.response?.status === 404 ? "Event not found." : "Failed to load event.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  // Reset booking state when switching events
  useEffect(() => {
    setQuantity(1);
    setEmail("");
  }, [eventId]);

  useEffect(() => {
    if (searchParams.get("cancelled") === "1") {
      toast.info("Checkout cancelled. You can book again any time.");
    }
  }, [searchParams]);

  const soldOut = event?.availability === "sold_out";

  const totalPrice = useMemo(() => {
    if (!event?.price) return null;
    // Parse a leading "£NN" from "£18" or "£45" or "From £20"
    const m = String(event.price).match(/£\s?(\d+(?:\.\d+)?)/);
    if (!m) return null;
    return Number(m[1]) * quantity;
  }, [event, quantity]);

  const startStripeCheckout = async () => {
    if (!event) return;
    if (soldOut) {
      toast.info("This experience is sold out. Join the waitlist for the next one.");
      return;
    }
    setBookingLoading(true);
    try {
      const { data } = await axios.post(`${API}/checkout`, {
        event_id: event.id,
        quantity,
        origin_url: window.location.origin,
        email: email || null,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || "Checkout failed. Please try again.");
    } finally {
      setBookingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center text-zinc-400 gap-3">
        <Loader2 className="h-5 w-5 animate-spin text-amber-400" /> Loading…
      </div>
    );
  }

  if (error || !event) {
    return (
      <div
        data-testid="event-detail-error"
        className="min-h-screen pt-32 pb-20 px-6 text-center"
      >
        <p className="text-amber-400 text-[11px] uppercase tracking-[0.35em] font-bold mb-6">
          Lost the thread
        </p>
        <h1 className="font-display font-black uppercase text-5xl">
          {error || "Event not found"}
        </h1>
        <Link
          to="/whats-on"
          className="mt-10 inline-flex items-center bg-amber-400 hover:bg-amber-300 text-black px-8 py-5 font-bold uppercase tracking-[0.25em] text-xs"
        >
          ← Back to What's On
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="event-detail-page" className="pb-24 lg:pb-0">
      <SEOHead
        title={`${event.title} · ${event.category} · Temple of Kulture`}
        description={`${event.description} ${event.location} · ${formatDate(event.date)} · ${event.time} · ${event.price}.`}
        image={event.image}
        type="event"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Event",
          name: event.title,
          startDate: event.date,
          description: event.long_description || event.description,
          location: { "@type": "Place", name: event.location },
          image: event.image,
          offers: {
            "@type": "Offer",
            price: String(event.price || "").replace(/[^\d.]/g, "") || undefined,
            priceCurrency: "GBP",
            availability:
              event.availability === "sold_out"
                ? "https://schema.org/SoldOut"
                : "https://schema.org/InStock",
          },
          organizer: { "@type": "Organization", name: "Temple of Kulture" },
        }}
      />
      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{ backgroundImage: `url(${event.image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent" />

        <Container className="relative pt-44 pb-16 w-full">
          <Link
            to="/whats-on"
            data-testid="event-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-amber-400 hover:gap-3 transition-all mb-8"
          >
            <ArrowLeft className="h-4 w-4" /> Back to What's On
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-4xl"
          >
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="text-[11px] uppercase tracking-[0.35em] text-amber-400 font-bold">
                {event.category}
              </span>
              {event.tag && !soldOut && (
                <span className="bg-amber-400 text-black text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1">
                  {event.tag}
                </span>
              )}
              {event.availability === "low" && (
                <span
                  data-testid="event-low-availability-badge"
                  className="bg-amber-400/95 text-black text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 tok-pulse-gold"
                >
                  Low availability
                </span>
              )}
              {soldOut && (
                <span
                  data-testid="event-sold-out-badge"
                  className="bg-white text-black text-[10px] uppercase tracking-[0.3em] font-bold px-2.5 py-1"
                >
                  Sold out
                </span>
              )}
            </div>
            <h1 className="font-display font-black uppercase leading-[0.92] tracking-tighter text-5xl sm:text-6xl lg:text-7xl">
              {event.title}
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-zinc-300 max-w-2xl leading-relaxed">
              {event.description}
            </p>
          </motion.div>
        </Container>
      </section>

      {/* DETAILS */}
      <section className="py-24 sm:py-32">
        <Container>
          <div className="grid lg:grid-cols-12 gap-12">
            {/* Main column */}
            <div className="lg:col-span-7 space-y-16">
              <FadeIn>
                <SectionLabel>About this experience</SectionLabel>
                <p className="mt-6 text-zinc-200 text-lg leading-relaxed">
                  {event.long_description || event.description}
                </p>
              </FadeIn>

              {event.schedule?.length > 0 && (
                <FadeIn>
                  <SectionLabel>Schedule</SectionLabel>
                  <ul className="mt-6 space-y-3">
                    {event.schedule.map((s, i) => (
                      <li
                        key={i}
                        data-testid={`event-schedule-${i}`}
                        className="border border-white/5 bg-zinc-950 px-5 py-4 text-zinc-200 flex gap-4"
                      >
                        <span className="text-amber-400 font-display font-bold w-8 shrink-0">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="leading-relaxed">{s}</span>
                      </li>
                    ))}
                  </ul>
                </FadeIn>
              )}

              {event.accessibility?.length > 0 && (
                <FadeIn>
                  <SectionLabel>Accessibility</SectionLabel>
                  <div className="mt-6 border border-amber-400/20 bg-zinc-950 p-6 sm:p-8">
                    <div className="flex items-start gap-4">
                      <Accessibility className="h-6 w-6 text-amber-400 shrink-0 mt-1" />
                      <ul className="space-y-3 text-zinc-200">
                        {event.accessibility.map((a, i) => (
                          <li
                            key={i}
                            data-testid={`event-accessibility-${i}`}
                            className="leading-relaxed"
                          >
                            — {a}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </FadeIn>
              )}

              {event.gallery?.length > 0 && (
                <FadeIn>
                  <SectionLabel>Gallery</SectionLabel>
                  <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                    {event.gallery.map((g, i) => (
                      <div
                        key={i}
                        data-testid={`event-gallery-${i}`}
                        className="relative aspect-square overflow-hidden border border-white/5 group"
                      >
                        <div
                          className="absolute inset-0 bg-cover bg-center transition-transform duration-[1500ms] group-hover:scale-110"
                          style={{ backgroundImage: `url(${g})` }}
                        />
                      </div>
                    ))}
                  </div>
                </FadeIn>
              )}
            </div>

            {/* Booking sidebar */}
            <aside className="lg:col-span-5">
              <div className="lg:sticky lg:top-28">
                <FadeIn>
                  <div className="border border-amber-400/30 bg-zinc-950 p-8">
                    <SectionLabel>Booking</SectionLabel>
                    <p className="mt-6 font-display font-black text-4xl sm:text-5xl text-amber-400">
                      {event.price}
                    </p>

                    <ul className="mt-8 space-y-3 text-sm text-zinc-300">
                      <Detail Icon={Calendar} label={formatDate(event.date)} />
                      <Detail Icon={Clock} label={event.time} />
                      <Detail Icon={MapPin} label={event.location} />
                      <Detail
                        Icon={Ticket}
                        label={
                          event.booking_type === "stripe"
                            ? "Secure checkout via Stripe"
                            : event.booking_type === "acuity"
                            ? "Book via Acuity Scheduling"
                            : "Custom quote"
                        }
                      />
                    </ul>

                    {/* Stripe quantity + email */}
                    {event.booking_type === "stripe" && !soldOut && (
                      <div className="mt-8 space-y-5">
                        <div>
                          <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-3 block font-bold">
                            Tickets
                          </label>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              data-testid="qty-decrement"
                              onClick={() =>
                                setQuantity((q) => Math.max(1, q - 1))
                              }
                              disabled={quantity <= 1}
                              className="h-12 w-12 border border-white/10 hover:border-amber-400 hover:text-amber-400 disabled:opacity-40 flex items-center justify-center transition-colors"
                              aria-label="Decrease tickets"
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                            <div
                              data-testid="qty-value"
                              className="flex-1 text-center font-display font-bold text-2xl border border-white/10 h-12 flex items-center justify-center"
                            >
                              {quantity}
                            </div>
                            <button
                              type="button"
                              data-testid="qty-increment"
                              onClick={() =>
                                setQuantity((q) => Math.min(MAX_QTY, q + 1))
                              }
                              disabled={quantity >= MAX_QTY}
                              className="h-12 w-12 border border-white/10 hover:border-amber-400 hover:text-amber-400 disabled:opacity-40 flex items-center justify-center transition-colors"
                              aria-label="Increase tickets"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                          {totalPrice !== null && quantity > 1 && (
                            <p
                              data-testid="qty-total-price"
                              className="mt-3 text-xs text-zinc-400 uppercase tracking-[0.2em]"
                            >
                              Total: <span className="text-amber-400 font-bold">£{totalPrice.toFixed(2)}</span>
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-3 block font-bold">
                            Email (optional)
                          </label>
                          <input
                            type="email"
                            data-testid="booking-email-input"
                            placeholder="for confirmation & reminders"
                            value={email}
                            onChange={(ev) => setEmail(ev.target.value)}
                            className="w-full bg-black border border-white/10 px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-amber-400 transition-colors text-sm"
                          />
                        </div>
                      </div>
                    )}

                    <div className="mt-8">
                      <BookingCta
                        event={event}
                        soldOut={soldOut}
                        bookingLoading={bookingLoading}
                        onStripe={startStripeCheckout}
                      />
                    </div>

                    {event.booking_type === "acuity" && !soldOut && (
                      <p className="mt-4 text-xs text-zinc-500 leading-relaxed">
                        You'll be taken to our Acuity scheduling page to choose
                        a time slot.
                      </p>
                    )}
                    {event.booking_type === "stripe" && !soldOut && (
                      <p className="mt-4 text-xs text-zinc-500 leading-relaxed">
                        Payment is processed securely by Stripe. Confirmation
                        emailed instantly.
                      </p>
                    )}
                    {event.booking_type === "enquiry" && (
                      <p className="mt-4 text-xs text-zinc-500 leading-relaxed">
                        This is a bespoke commission. We'll come back with a
                        proposal within 2 working days.
                      </p>
                    )}
                    {soldOut && (
                      <p className="mt-4 text-xs text-zinc-500 leading-relaxed">
                        This experience has sold out. Join the mailing list to
                        get first access to the next date.
                      </p>
                    )}
                  </div>

                  <Link
                    to="/whats-on"
                    className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-zinc-400 hover:text-amber-400 transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Browse more experiences
                  </Link>
                </FadeIn>
              </div>
            </aside>
          </div>

          {/* RELATED EXPERIENCES */}
          {related.length > 0 && (
            <div className="mt-28 sm:mt-36 border-t border-white/5 pt-20">
              <FadeIn>
                <SectionLabel>More in {event.category}</SectionLabel>
                <h2 className="mt-6 font-display font-black uppercase leading-[0.95] tracking-tighter text-3xl sm:text-4xl lg:text-5xl">
                  Keep exploring <br />
                  <span className="text-amber-400">the world.</span>
                </h2>
              </FadeIn>
              <div className="mt-14 grid md:grid-cols-3 gap-5">
                {related.map((r, i) => (
                  <FadeIn key={r.id} delay={i * 0.06}>
                    <Link
                      to={`/whats-on/${r.id}`}
                      data-testid={`related-event-${r.id}`}
                      className="group block border border-white/5 hover:border-amber-400/40 bg-zinc-950 transition-colors overflow-hidden h-full"
                    >
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <div
                          className="absolute inset-0 bg-cover bg-center transition-transform duration-[1500ms] group-hover:scale-110"
                          style={{ backgroundImage: `url(${r.image})` }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                        {r.availability === "sold_out" && (
                          <span className="absolute top-3 left-3 bg-white text-black text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1">
                            Sold out
                          </span>
                        )}
                      </div>
                      <div className="p-5">
                        <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">
                          {formatDate(r.date)}
                        </p>
                        <h3 className="mt-3 font-display font-bold text-lg leading-tight">
                          {r.title}
                        </h3>
                        <p className="mt-2 text-xs text-zinc-400 line-clamp-2 leading-relaxed">
                          {r.description}
                        </p>
                      </div>
                    </Link>
                  </FadeIn>
                ))}
              </div>
            </div>
          )}
        </Container>
      </section>

      {/* STICKY MOBILE BOOKING BAR */}
      <div
        data-testid="mobile-book-bar"
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-black/95 backdrop-blur-xl border-t border-amber-400/20 px-4 py-3 flex items-center gap-3"
      >
        <div className="flex-1 min-w-0">
          <p className="text-amber-400 font-display font-bold text-lg leading-none">
            {event.price}
          </p>
          <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-zinc-400 truncate">
            {event.title}
          </p>
        </div>
        <MobileBookButton
          event={event}
          soldOut={soldOut}
          bookingLoading={bookingLoading}
          onStripe={startStripeCheckout}
        />
      </div>
    </div>
  );
}

function Detail({ Icon, label }) {
  return (
    <li className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
      <span className="leading-relaxed">{label}</span>
    </li>
  );
}

function BookingCta({ event, soldOut, bookingLoading, onStripe }) {
  if (soldOut) {
    return (
      <Link
        to="/#newsletter"
        data-testid="event-waitlist-cta"
        className="w-full inline-flex items-center justify-center gap-3 bg-zinc-900 border border-amber-400/40 text-amber-400 px-6 py-5 font-bold uppercase tracking-[0.25em] text-xs hover:bg-amber-400/10 transition-colors"
      >
        Join the waitlist <Sparkles className="h-4 w-4" />
      </Link>
    );
  }
  if (event.booking_type === "stripe") {
    return (
      <button
        onClick={onStripe}
        disabled={bookingLoading}
        data-testid="event-stripe-book-button"
        className="w-full inline-flex items-center justify-center gap-3 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black px-6 py-5 font-bold uppercase tracking-[0.25em] text-xs transition-colors"
      >
        {bookingLoading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Redirecting…
          </>
        ) : (
          <>
            Book with Stripe <ArrowUpRight className="h-4 w-4" />
          </>
        )}
      </button>
    );
  }
  if (event.booking_type === "acuity") {
    return (
      <a
        href={event.booking_url || "#"}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="event-acuity-book-button"
        className="w-full inline-flex items-center justify-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-6 py-5 font-bold uppercase tracking-[0.25em] text-xs transition-colors"
      >
        Book on Acuity <ExternalLink className="h-4 w-4" />
      </a>
    );
  }
  return (
    <Link
      to="/corporate"
      data-testid="event-enquiry-cta"
      className="w-full inline-flex items-center justify-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-6 py-5 font-bold uppercase tracking-[0.25em] text-xs transition-colors"
    >
      Request a quote <ArrowUpRight className="h-4 w-4" />
    </Link>
  );
}

function MobileBookButton({ event, soldOut, bookingLoading, onStripe }) {
  const cls =
    "shrink-0 inline-flex items-center justify-center gap-2 px-5 py-3.5 font-bold uppercase tracking-[0.2em] text-[11px] transition-colors";
  if (soldOut) {
    return (
      <Link
        to="/whats-on"
        data-testid="mobile-book-button"
        className={`${cls} bg-zinc-800 text-zinc-300`}
      >
        Waitlist
      </Link>
    );
  }
  if (event.booking_type === "stripe") {
    return (
      <button
        onClick={onStripe}
        disabled={bookingLoading}
        data-testid="mobile-book-button"
        className={`${cls} bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black`}
      >
        {bookingLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Book"}
      </button>
    );
  }
  if (event.booking_type === "acuity") {
    return (
      <a
        href={event.booking_url || "#"}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="mobile-book-button"
        className={`${cls} bg-amber-400 hover:bg-amber-300 text-black`}
      >
        Acuity
      </a>
    );
  }
  return (
    <Link
      to="/corporate"
      data-testid="mobile-book-button"
      className={`${cls} bg-amber-400 hover:bg-amber-300 text-black`}
    >
      Enquire
    </Link>
  );
}
